import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { RevenueRuleRow, RevenueRuleSetRow } from '../../api';
import { eventEndpoints } from '../../api';
import { Badge, Button, Panel } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, humanize } from './shared';
import { QuickBooksProjectCard } from './QuickBooksProjectCard';
import { useFeatures } from '../../features';

const COMPONENTS = ['FACE_VALUE', 'FACILITY_FEE', 'PROMOTER_FEE', 'ONLINE_CONVENIENCE_FEE', 'ORDER_FEE', 'PROCESSING_FEE', 'DELIVERY_FEE', 'CUSTOM_FEE'];
const LINE_TYPES = ['TICKET_SALES', 'TICKET_SALES_PAYABLE', 'FACILITY_FEE', 'PROMOTER_FEE', 'ONLINE_CONVENIENCE_FEE', 'ORDER_FEE', 'DELIVERY_FEE', 'CUSTOM_FEE', 'PROCESSING_FEE_CHARGED'];
const OWNERS = ['BRE', 'RENTER', 'PROMOTER', 'CO_PROMOTER', 'ARTIST', 'CHARITY', 'OTHER'];
const CODES = ['TICKET_SALES', 'TICKET_SALES_PAYABLE', 'FACILITY_FEE', 'PROMOTER_FEE', 'PROMOTER_FEE_PAYABLE', 'ONLINE_CONVENIENCE_FEE', 'ORDER_FEE', 'DELIVERY_FEE', 'CUSTOM_FEE', 'PROCESSING_FEE_CHARGED'];
const BEHAVIORS = ['RETAINED_BY_BRE', 'PAYABLE_TO_OWNER', 'DEDUCTED_FROM_PAYABLE', 'PASS_THROUGH'];
const MODES = ['BRE_PROMOTED', 'RENTAL', 'CO_PROMOTE', 'CUSTOM'];

/**
 * Whose money it is. The active rule set is what sales snapshot; a change is
 * a new version that has to balance (10,000 basis points per component)
 * before it can be activated.
 */
export function AccountingPanel({ detail, refresh, onError, can }: PanelProps) {
  const features = useFeatures();
  const editable = can('accounting_treatment.manage') && detail.event.status !== 'ARCHIVED';
  const sets = useQuery({ queryKey: ['revenue-rule-sets', detail.event.id], queryFn: () => eventEndpoints.revenueRuleSets(detail.event.id) });
  const organizations = useQuery({ queryKey: ['organizations'], queryFn: eventEndpoints.organizations });
  const [mode, setMode] = useState('');
  const list: RevenueRuleSetRow[] = sets.data?.rule_sets ?? [];
  const draft = list.find((s) => s.status === 'DRAFT') ?? null;
  const active = list.find((s) => s.status === 'ACTIVE') ?? null;

  const createDraft = useMutation({
    mutationFn: () => eventEndpoints.draftRevenueRuleSet(detail.event.id, mode || undefined),
    onSuccess: () => void sets.refetch(),
    onError,
  });
  const activate = useMutation({
    mutationFn: (setId: string) => eventEndpoints.activateRevenueRuleSet(detail.event.id, setId),
    onSuccess: () => {
      void sets.refetch();
      refresh();
    },
    onError,
  });

  return (
    <div className="space-y-4">
      {/* Hidden with the rest of QuickBooks (D-369). */}
      {features.quickbooks ? <QuickBooksProjectCard eventId={detail.event.id} can={can} onError={onError} /> : null}
      <Panel
        title="Accounting treatment"
        description="Each revenue component is split among owners in basis points totaling 10,000. Face value is required; a fee component here overrides the fee rule's own owner."
        actions={
          editable && !draft ? (
            <div className="flex gap-2">
              <select value={mode} onChange={(e) => setMode(e.target.value)} className={INPUT} aria-label="Start from">
                <option value="">copy the active set</option>
                {MODES.map((m) => (
                  <option key={m} value={m}>
                    start from {humanize(m)} defaults
                  </option>
                ))}
              </select>
              <Button variant="primary" onClick={() => createDraft.mutate()} disabled={createDraft.isPending}>
                Draft a new version
              </Button>
            </div>
          ) : null
        }
      >
        <div className="space-y-4 px-5 py-4">
          {list.length === 0 ? <p className="text-sm text-ink-400">Loading…</p> : null}
          {draft ? (
            <RuleSetEditor
              detail={detail}
              set={draft}
              organizations={organizations.data?.organizations ?? []}
              onSaved={() => void sets.refetch()}
              onActivate={() => activate.mutate(draft.id)}
              onError={onError}
            />
          ) : null}
          {active ? <RuleSetView set={active} title={`Active: v${active.version} (${humanize(active.accounting_mode)})`} /> : null}
          {list
            .filter((s) => s.status === 'SUPERSEDED')
            .map((s) => (
              <RuleSetView key={s.id} set={s} title={`Superseded: v${s.version} (${humanize(s.accounting_mode)})`} muted />
            ))}
        </div>
      </Panel>
    </div>
  );
}

function RuleSetView({ set, title, muted }: { set: RevenueRuleSetRow; title: string; muted?: boolean }) {
  return (
    <div className={muted ? 'opacity-60' : ''}>
      <p className="flex items-center gap-2 text-sm font-semibold text-ink-900">
        {title} <Badge tone={set.status === 'ACTIVE' ? 'ok' : 'neutral'}>{set.status}</Badge>
      </p>
      <table className="mt-1 text-sm">
        <tbody>
          {set.rules.map((r, i) => (
            <tr key={r.id ?? i}>
              <td className="pr-4 py-0.5 text-ink-600">{humanize(r.revenue_component)}</td>
              <td className="pr-4 py-0.5 tabular font-medium">{(r.basis_points / 100).toFixed(2)}%</td>
              <td className="pr-4 py-0.5">
                {humanize(r.revenue_owner_type)}
                {r.organization_name ? ` · ${r.organization_name}` : ''}
              </td>
              <td className="pr-4 py-0.5 text-ink-600">as {humanize(r.line_type)}</td>
              <td className="py-0.5 text-ink-400">{humanize(r.settlement_behavior)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RuleSetEditor({ detail, set, organizations, onSaved, onActivate, onError }: { detail: PanelProps['detail']; set: RevenueRuleSetRow; organizations: { id: string; name: string }[]; onSaved: () => void; onActivate: () => void; onError: PanelProps['onError'] }) {
  const [rules, setRules] = useState<RevenueRuleRow[]>(set.rules.map((r) => ({ ...r })));
  const save = useMutation({
    mutationFn: () => eventEndpoints.setRevenueRules(detail.event.id, set.id, rules.map((r) => ({ ...r, revenue_owner_organization_id: r.revenue_owner_type === 'BRE' ? null : r.revenue_owner_organization_id }))),
    onSuccess: onSaved,
    onError,
  });
  const totals = COMPONENTS.map((c) => ({ c, total: rules.filter((r) => r.revenue_component === c).reduce((n, r) => n + r.basis_points, 0) })).filter((t) => t.total > 0);
  const balanced = totals.every((t) => t.total === 10000) && totals.some((t) => t.c === 'FACE_VALUE');
  const update = (i: number, patch: Partial<RevenueRuleRow>) => setRules(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="rounded border border-accent/30 bg-accent-soft/40 p-3">
      <p className="text-sm font-semibold text-ink-900">
        Draft v{set.version} ({humanize(set.accounting_mode)})
      </p>
      <table className="mt-2 text-sm">
        <thead>
          <tr className="text-left text-xs font-semibold tracking-wide text-ink-400 uppercase">
            <th className="pr-2">Component</th>
            <th className="pr-2">Basis pts</th>
            <th className="pr-2">Owner</th>
            <th className="pr-2">Organization</th>
            <th className="pr-2">Posts as</th>
            <th className="pr-2">Code</th>
            <th className="pr-2">Settlement</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rules.map((r, i) => (
            <tr key={i}>
              <td className="pr-2 py-0.5">
                <select value={r.revenue_component} onChange={(e) => update(i, { revenue_component: e.target.value })} className={INPUT}>
                  {COMPONENTS.map((c) => (
                    <option key={c} value={c}>
                      {humanize(c)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="pr-2 py-0.5">
                <input value={r.basis_points} onChange={(e) => update(i, { basis_points: Number(e.target.value.replace(/\D/g, '') || 0) })} inputMode="numeric" className={`tabular w-20 ${INPUT}`} aria-label="Basis points" />
              </td>
              <td className="pr-2 py-0.5">
                <select value={r.revenue_owner_type} onChange={(e) => update(i, { revenue_owner_type: e.target.value })} className={INPUT}>
                  {OWNERS.map((o) => (
                    <option key={o} value={o}>
                      {humanize(o)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="pr-2 py-0.5">
                <select value={r.revenue_owner_organization_id ?? ''} onChange={(e) => update(i, { revenue_owner_organization_id: e.target.value || null })} disabled={r.revenue_owner_type === 'BRE'} className={INPUT}>
                  <option value="">—</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </td>
              <td className="pr-2 py-0.5">
                <select value={r.line_type} onChange={(e) => update(i, { line_type: e.target.value })} className={INPUT}>
                  {LINE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {humanize(t)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="pr-2 py-0.5">
                <select value={r.accounting_code} onChange={(e) => update(i, { accounting_code: e.target.value })} className={INPUT}>
                  {CODES.map((c) => (
                    <option key={c} value={c}>
                      {humanize(c)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="pr-2 py-0.5">
                <select value={r.settlement_behavior} onChange={(e) => update(i, { settlement_behavior: e.target.value })} className={INPUT}>
                  {BEHAVIORS.map((b) => (
                    <option key={b} value={b}>
                      {humanize(b)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="py-0.5">
                <button type="button" className="text-ink-400 hover:text-bad" aria-label="Remove rule" onClick={() => setRules(rules.filter((_, j) => j !== i))}>
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
        <Button
          onClick={() =>
            setRules([
              ...rules,
              { revenue_component: 'FACE_VALUE', line_type: 'TICKET_SALES_PAYABLE', revenue_owner_type: 'RENTER', revenue_owner_organization_id: detail.event.renter_organization_id, basis_points: 0, accounting_code: 'TICKET_SALES_PAYABLE', settlement_behavior: 'PAYABLE_TO_OWNER', rounding_priority: rules.length },
            ])
          }
        >
          Add a share
        </Button>
        {totals.map((t) => (
          <span key={t.c} className={t.total === 10000 ? 'text-ok' : 'text-bad'}>
            {humanize(t.c)}: {t.total}/10000
          </span>
        ))}
        <span className="mx-1 text-ink-400">|</span>
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Save draft
        </Button>
        <Button variant="primary" onClick={onActivate} disabled={!balanced || save.isPending} title={balanced ? undefined : 'Every component must total 10,000 basis points and face value must be present'}>
          Activate
        </Button>
      </div>
    </div>
  );
}
